import { groupBy } from 'lodash';
import { EventMarketItemType } from 'src_common/common/websocket2/modelsApi/EventMarkets';
import { getLineGroupingName } from './getLineGroupingName';
import { GroupedEventMarkets } from './shared';

export const LINE_GROUPED_PREFIX = 'line-grouped:';

const GOALSCORER_GROUP_TEMPLATES: ReadonlySet<string> = new Set([
    'first-goalscorer',
    'anytime-goalscorer',
    'hattrick-goalscorer',
    'player-to-score-a-hat-trick',
    'anytime-touchdown-scorer',
    'first-touchdown-scorer',
    'last-touchdown-scorer',
]);

const isFlavorless = (market: EventMarketItemType): boolean => market.templateId === 'flavorless-template';

const hasKeyLine = (market: EventMarketItemType): boolean => market.keyLine !== null && market.keyLine !== undefined;

const isActiveAndDisplayed = (market: EventMarketItemType): boolean => {
    return market.display === true && market.active === true;
};

const isHomeOrAwayFirstGoalscorer = (templateId: string): boolean => {
    return templateId === 'home-team-first-goalscorer' || templateId === 'away-team-first-goalscorer';
};

const isFirstGoalscorerOnlyVariant = (templateId: string, marketName: string): boolean => {
    return templateId.includes('first-goalscorer-only') || /first goalscorer \(only/i.test(marketName);
};

const isClassicFirstGoalscorer = (templateId: string, marketName: string): boolean => {
    if (isFirstGoalscorerOnlyVariant(templateId, marketName) || isHomeOrAwayFirstGoalscorer(templateId)) {
        return false;
    }
    return templateId === 'first-goalscorer' || /^first goalscorer$/i.test(marketName.trim());
};

const hasClassicFirstGoalscorerMarket = (markets: Array<EventMarketItemType>): boolean => {
    return markets.some((market) => isClassicFirstGoalscorer(market.templateId, market.name));
};

const getGoalscorerGroupKey = (market: EventMarketItemType, hasClassicFirst: boolean): string | null => {
    const { templateId, name } = market;

    if (isHomeOrAwayFirstGoalscorer(templateId)) {
        return `goalscorer-standalone-${templateId}`;
    }

    if (isFirstGoalscorerOnlyVariant(templateId, name)) {
        return hasClassicFirst ? `goalscorer-standalone-${templateId}` : 'goalscorer';
    }

    if (GOALSCORER_GROUP_TEMPLATES.has(templateId)) {
        return 'goalscorer';
    }

    return null;
};

const dedupeFlavorlessByName = (markets: Array<EventMarketItemType>): Array<EventMarketItemType> => {
    const namesWithRealTemplate = new Set<string>();

    for (const market of markets) {
        if (!isFlavorless(market)) {
            namesWithRealTemplate.add(market.name);
        }
    }

    return markets.filter((market) => !(isFlavorless(market) && namesWithRealTemplate.has(market.name)));
};

const filterSuspendedLineFamilyMembers = (markets: Array<EventMarketItemType>): Array<EventMarketItemType> => {
    const familyCounts = new Map<string, number>();

    for (const market of markets) {
        if (!hasKeyLine(market)) {
            continue;
        }
        const groupingName = getLineGroupingName(market.name, market.keyLine);
        familyCounts.set(groupingName, (familyCounts.get(groupingName) ?? 0) + 1);
    }

    return markets.filter((market) => {
        if (isActiveAndDisplayed(market) === true || !hasKeyLine(market)) {
            return true;
        }
        const familySize = familyCounts.get(getLineGroupingName(market.name, market.keyLine)) ?? 0;
        return familySize < 2;
    });
};

const getLineGroupedNames = (
    markets: Array<EventMarketItemType>,
    options: { countOnlyActive: boolean; minCount: number }
): Set<string> => {
    const counts = new Map<string, number>();

    for (const market of markets) {
        if (!hasKeyLine(market)) {
            continue;
        }
        if (options.countOnlyActive === true && isActiveAndDisplayed(market) === false) {
            continue;
        }
        const groupingName = getLineGroupingName(market.name, market.keyLine);
        counts.set(groupingName, (counts.get(groupingName) ?? 0) + 1);
    }

    const eligible = new Set<string>();
    for (const [name, count] of counts) {
        if (count >= options.minCount) {
            eligible.add(name);
        }
    }

    return eligible;
};

export type GroupMarketsUnifiedOptions = {
    isInPlay?: boolean;
    /**
     * Min key-line markets sharing a stripped name before using LineGroupedMarketGroup.
     * Use `1` after sticky-slot coalesce (one market left per group).
     */
    lineGroupMinCount?: number;
};

export const groupMarketsUnified = (
    markets: Array<EventMarketItemType>,
    options: GroupMarketsUnifiedOptions = {}
): GroupedEventMarkets => {
    const isInPlay = options.isInPlay === true;
    const lineGroupMinCount = options.lineGroupMinCount ?? 2;
    const deduped = dedupeFlavorlessByName(markets);
    const visibleMarkets = isInPlay ? deduped : filterSuspendedLineFamilyMembers(deduped);
    const lineGroupedNames = getLineGroupedNames(visibleMarkets, {
        countOnlyActive: isInPlay === false,
        minCount: lineGroupMinCount,
    });
    const hasClassicFirst = hasClassicFirstGoalscorerMarket(visibleMarkets);

    return groupBy(visibleMarkets, (market) => {
        const goalscorerKey = getGoalscorerGroupKey(market, hasClassicFirst);
        if (goalscorerKey !== null) {
            return goalscorerKey;
        }

        if (!hasKeyLine(market)) {
            return market.name;
        }

        const groupingName = getLineGroupingName(market.name, market.keyLine);
        if (lineGroupedNames.has(groupingName)) {
            return `${LINE_GROUPED_PREFIX}${groupingName}`;
        }

        return market.name;
    });
};
